/**
 * PHARMAKON — Journal des achats : logique pure de la colonne « note » et de l'invalidation
 * ciblée (cross-module X XWP-26, 02_DESIGN §2.1 / §2.6, XA-04, XA-05, XA-17).
 *
 *   · `routeLigneAchat`  — où mène un clic / Entrée sur la ligne (XD-26) : brouillon → éditeur,
 *                          sinon la facture.
 *   · `etatNote`         — l'état de la bulle d'aperçu : ouverte au survol OU au focus OU après un
 *                          clic sur l'icône ; Échap la ferme jusqu'au prochain survol / focus.
 *   · `clicIconeNote`    — le clic sur l'icône n'atteint jamais la ligne (stopPropagation) et ouvre
 *                          l'aperçu à la place.
 *   · `placerBulle`      — la position (fixe, dans la fenêtre) de la bulle sous l'icône, au-dessus
 *                          quand la place manque, jamais hors de l'écran.
 *   · `lignesNommees`    — l'événement d'invalidation nomme-t-il une ligne affichée ?
 *   · `brouillonSupprimable` — la ligne porte-t-elle « Supprimer le brouillon » (refonte WP-64, D-26) ?
 */
import {
  cleAchat,
  ETAT_ECART_TTC,
  GROUPEMENT_LIGNES_ACHAT,
  M_S4,
  ONGLETS_JOURNAL,
} from "@/config/achat-journal.config.js";
import { fmtMoney } from "@/config/locale.config.js";

/**
 * La route d'une ligne du journal (XD-26).
 * @param {{ id: string, statut?: string }} achat
 * @returns {string}
 */
export function routeLigneAchat(achat) {
  return achat.statut === "BROUILLON" ? `/achats/${achat.id}/modifier` : `/achats/${achat.id}`;
}

/**
 * Refonte WP-64 (D-26, CP §3.1-9) — « Supprimer le brouillon » sur une ligne du journal : un
 * brouillon seulement, et seulement pour un rôle qui a `achat:delete` (droits inchangés). Une facture validée ne
 * se supprime jamais (rectification, avoir ou — WP-72 — annulation) ; le serveur refuse de même.
 * @param {{ statut?: string }|null|undefined} achat
 * @param {boolean} peutSupprimer `can(user, "achat", "delete")`
 */
export function brouillonSupprimable(achat, peutSupprimer) {
  return Boolean(peutSupprimer && achat && achat.statut === "BROUILLON");
}

/** État initial de la bulle : fermée. */
export const NOTE_FERMEE = Object.freeze({ survol: false, focus: false, epingle: false, rejetee: false });

/**
 * Réducteur de la bulle d'aperçu. Événements : `entrer` / `sortir` (pointeur), `focus` / `blur`,
 * `clic` (épingle l'aperçu), `echap` (ferme jusqu'au prochain survol ou focus).
 * @param {typeof NOTE_FERMEE} etat
 * @param {"entrer"|"sortir"|"focus"|"blur"|"clic"|"echap"} evenement
 * @returns {typeof NOTE_FERMEE}
 */
export function etatNote(etat, evenement) {
  switch (evenement) {
    case "entrer":
      return { ...etat, survol: true, rejetee: false };
    case "sortir":
      return { ...etat, survol: false, epingle: etat.focus ? etat.epingle : false };
    case "focus":
      return { ...etat, focus: true, rejetee: false };
    case "blur":
      return { ...etat, focus: false, epingle: false };
    case "clic":
      return { ...etat, epingle: true, rejetee: false };
    case "echap":
      return { ...etat, epingle: false, rejetee: true };
    default:
      return etat;
  }
}

/** La bulle est-elle visible ? */
export function noteOuverte(etat) {
  return !etat.rejetee && (etat.survol || etat.focus || etat.epingle);
}

/**
 * Le clic sur l'icône : il ne remonte pas à la ligne (qui ouvrirait la facture) et ouvre l'aperçu.
 * @param {{ stopPropagation: () => void, preventDefault?: () => void }} e
 * @param {(evenement: "clic") => void} envoyer
 */
export function clicIconeNote(e, envoyer) {
  e.stopPropagation();
  envoyer("clic");
}

/**
 * Position fixe de la bulle : sous l'icône, alignée sur son bord gauche ; au-dessus quand elle ne
 * tient pas dessous et qu'il y a plus de place au-dessus ; décalée pour rester dans la fenêtre.
 * @param {{ top: number, bottom: number, left: number }} ancre  rectangle de l'icône
 * @param {{ width: number, height: number }} bulle
 * @param {{ width: number, height: number }} fenetre
 * @param {number} ecart
 * @returns {{ top: number, left: number, cote: "dessous"|"dessus" }}
 */
export function placerBulle(ancre, bulle, fenetre, ecart) {
  const dessous = ancre.bottom + ecart;
  const placeDessous = fenetre.height - dessous - ecart;
  const placeDessus = ancre.top - ecart - ecart;
  const auDessus = bulle.height > placeDessous && placeDessus > placeDessous;
  const top = auDessus ? Math.max(ecart, ancre.top - ecart - bulle.height) : dessous;
  const maxLeft = Math.max(ecart, fenetre.width - ecart - bulle.width);
  const left = Math.min(Math.max(ecart, ancre.left), maxLeft);
  return { top, left, cote: auDessus ? "dessus" : "dessous" };
}

/**
 * L'événement `achats` (ou la réponse d'une écriture) nomme-t-il une ligne affichée ?
 * @param {string[]|undefined|null} cles  clés touchées (`achat:<id>`)
 * @param {Array<{ id: string }>} lignes
 * @returns {boolean}
 */
export function lignesNommees(cles, lignes) {
  if (!Array.isArray(cles) || cles.length === 0 || !Array.isArray(lignes)) return false;
  const nommees = new Set(cles);
  return lignes.some((l) => nommees.has(cleAchat(l.id)));
}

// ─── Refonte WP-52 — S4 / S5 (ux/02 §7, §8) ──────────────────────────────────────────────────

const da = (v) => fmtMoney(v, { noSuffix: true });
const deuxChiffres = (n) => String(n).padStart(2, "0");
/** A local calendar date as « YYYY-MM-DD ». */
export const isoLocal = (d) => `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}-${deuxChiffres(d.getDate())}`;

/**
 * A « Période » preset as an inclusive { from, to } of local dates (ux/02 §8.3).
 * @param {"aujourdhui"|"sept_jours"|"ce_mois"|"mois_precedent"|"ce_trimestre"|"cette_annee"} id
 * @param {Date} [maintenant]
 * @returns {{ from: string, to: string } | null}
 */
export function plagePeriode(id, maintenant = new Date()) {
  const j = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
  switch (id) {
    case "aujourdhui":
      return { from: isoLocal(j), to: isoLocal(j) };
    case "sept_jours":
      return { from: isoLocal(new Date(j.getFullYear(), j.getMonth(), j.getDate() - 6)), to: isoLocal(j) };
    case "ce_mois":
      return { from: isoLocal(new Date(j.getFullYear(), j.getMonth(), 1)), to: isoLocal(new Date(j.getFullYear(), j.getMonth() + 1, 0)) };
    case "mois_precedent":
      return { from: isoLocal(new Date(j.getFullYear(), j.getMonth() - 1, 1)), to: isoLocal(new Date(j.getFullYear(), j.getMonth(), 0)) };
    case "ce_trimestre": {
      // WP-30 (ux/03 §2.3) — the calendar quarter of today
      const debut = Math.floor(j.getMonth() / 3) * 3;
      return { from: isoLocal(new Date(j.getFullYear(), debut, 1)), to: isoLocal(new Date(j.getFullYear(), debut + 3, 0)) };
    }
    case "cette_annee":
      return { from: isoLocal(new Date(j.getFullYear(), 0, 1)), to: isoLocal(new Date(j.getFullYear(), 11, 31)) };
    default:
      return null;
  }
}

/**
 * The S4 tabs with the server counts (over the whole filter). No count while the counts are not a
 * trustworthy claim (loading, error) — a zero is a claim about the data.
 * @param {Record<string, number>|null|undefined} compteurs
 * @param {boolean} fiables
 */
export function ongletsJournal(compteurs, fiables) {
  return ONGLETS_JOURNAL.map((o) => ({
    value: o.value,
    label: o.label,
    count: fiables && compteurs ? compteurs[o.value] ?? 0 : undefined,
  }));
}

/**
 * The « Écart » cell of a S4 row (ux/02 §7.2, §2.3): « — » when conforme or not entered; the signed
 * TTC écart with its sentence otherwise.
 * @param {{ ecart_etat?: string, difference?: string|number|null }} achat
 * @returns {{ texte: string, titre: string, ton: "neutral"|"warning" }}
 */
export function celluleEcart(achat) {
  const etat = achat?.ecart_etat;
  const diff = achat?.difference;
  if (etat === ETAT_ECART_TTC.AVOIR_FINANCIER) {
    return { texte: `▲ ${fmtMoney(diff, { noSuffix: true, signed: true })}`, titre: M_S4.ecart[etat](da(diff)), ton: "warning" };
  }
  if (etat === ETAT_ECART_TTC.INFERIEUR) {
    const absolu = typeof diff === "string" ? diff.replace(/^-/, "") : Math.abs(Number(diff ?? 0));
    return { texte: `▼ ${fmtMoney(diff, { noSuffix: true, signed: true })}`, titre: M_S4.ecart[etat](da(absolu)), ton: "warning" };
  }
  return { texte: "—", titre: M_S4.ecart[etat] ?? M_S4.ecart[ETAT_ECART_TTC.CONFORME], ton: "neutral" };
}

/** The TTC shown on a S4 row: declared when present, else computed (OP-ACH-21). */
export const ttcAffiche = (achat) => achat?.montant_facture_reelle ?? achat?.montant_ttc ?? null;

/** The S4 footer sentence (ux/02 §7.2), over the whole filter. */
export function piedJournal(totaux) {
  if (!totaux) return "";
  return M_S4.pied({ factures: totaux.factures, ttc: da(totaux.montant_ttc), reste: da(totaux.reste_du) });
}

/**
 * The S4 query params from the filter values, the tab, the sort and the page — only what is set
 * (lists comma-joined: the server reads `a,b`).
 * @param {Record<string, unknown>} queryParams  `useFilters().queryParams`
 * @param {{ onglet: string, sort?: { by: string, order: string } | null, page: number, limit: number }} o
 */
export function paramsJournal(queryParams, { onglet, sort, page, limit }) {
  const out = {};
  for (const [k, v] of Object.entries(queryParams ?? {})) {
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(",");
    } else if (v !== "" && v != null) out[k] = v;
  }
  out.onglet = onglet;
  if (sort?.by) {
    out.sort_by = sort.by;
    out.sort_order = sort.order;
  }
  out.page = page;
  out.limit = limit;
  return out;
}

/**
 * S5 rows for the grid: flat without grouping; with a grouping, one group row per group present on
 * the page (its subtotal over the WHOLE filter, from `groupes`) holding the page's lines of that
 * group, in the server order.
 * @param {Array<{ id: string, groupe?: { cle: string, libelle: string } | null }>} lignes
 * @param {Array<{ cle: string, libelle: string }>} groupes
 * @param {string} groupement
 */
export function lignesEnGroupes(lignes, groupes, groupement) {
  if (!groupement || groupement === GROUPEMENT_LIGNES_ACHAT.AUCUN) return lignes ?? [];
  const sousTotaux = new Map((groupes ?? []).map((g) => [g.cle, g]));
  const out = [];
  const parCle = new Map();
  for (const l of lignes ?? []) {
    const cle = l.groupe?.cle ?? "";
    let g = parCle.get(cle);
    if (!g) {
      g = { id: `groupe:${cle}`, estGroupe: true, cle, libelle: l.groupe?.libelle ?? "", sousTotal: sousTotaux.get(cle) ?? null, enfants: [] };
      parCle.set(cle, g);
      out.push(g);
    }
    g.enfants.push(l);
  }
  return out;
}

/** The S3 route of a S5 line, the line highlighted (ux/02 §8.3). */
export const routeLigneDetail = (ligne) => `/achats/${ligne.facture.id}?ligne=${encodeURIComponent(ligne.id)}`;

/** S5 « Laboratoire / marque » option values: one multi-select, two server filters. */
export const valeurLaboratoire = (id) => `l:${id}`;
export const valeurMarque = (marque) => `m:${marque}`;

/**
 * The S5 query params: filters (lists comma-joined; the « Laboratoire / marque » selection split
 * into `laboratoire_id` and `marque`), grouping, page.
 * @param {Record<string, unknown>} queryParams  `useFilters().queryParams` (key `labo` for the brand)
 * @param {{ groupement: string, page?: number, limit?: number }} o
 */
export function paramsLignes(queryParams, { groupement, page, limit }) {
  const { labo, ...reste } = queryParams ?? {};
  const out = paramsJournal(reste, { onglet: undefined, sort: null, page, limit });
  delete out.onglet;
  if (page == null) delete out.page;
  if (limit == null) delete out.limit;
  const choix = Array.isArray(labo) ? labo : labo ? [labo] : [];
  const labos = choix.filter((v) => String(v).startsWith("l:")).map((v) => String(v).slice(2));
  const marques = choix.filter((v) => String(v).startsWith("m:")).map((v) => String(v).slice(2));
  if (labos.length) out.laboratoire_id = labos.join(",");
  if (marques.length) out.marque = marques.join(",");
  if (groupement && groupement !== GROUPEMENT_LIGNES_ACHAT.AUCUN) out.groupement = groupement;
  return out;
}

/** S5 footer sentence (ux/02 §8.1), over the whole filter. */
export function piedLignes(totaux, M) {
  if (!totaux) return "";
  return M.pied({
    lignes: totaux.lignes,
    quantite: totaux.quantite,
    ug: totaux.ug,
    recue: totaux.quantite_recue,
    totalHt: da(totaux.total_ht),
    valeurPpa: da(totaux.valeur_ppa),
    marge: totaux.marge_pct == null ? null : String(totaux.marge_pct).replace(".", ","),
  });
}
